import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';
import { EmailValidationService } from 'src/app/services/email-validation.service';
import {
  RegistrationService,
  RegistrationProfile
} from 'src/app/services/registration.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  standalone: false
})
export class SignupComponent implements OnInit {
  public form: FormGroup;
  public submitting = false;
  public done = false;
  public errors: string[] = [];

  public constructor(
    private _fb: FormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _emailValidation: EmailValidationService,
    private _confirmationService: ConfirmationService,
    private _registrationService: RegistrationService,
    private _authService: AuthService
  ) {}

  public ngOnInit(): void {
    this.form = this._fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [
        this._route.snapshot.queryParamMap.get('email') || '',
        [Validators.required, Validators.email]
      ],
      password: ['', [Validators.required, Validators.minLength(8)]],
      tos: [false, Validators.requiredTrue]
    });
  }

  public onSubmit(): void {
    if (this.form.invalid || this.submitting) return;

    this.errors = [];
    this.submitting = true;

    const email: string = this.form.value.email;

    this._emailValidation.validate(email).subscribe((validation) => {
      if (!validation.suspect) {
        this.register();
        return;
      }

      this._confirmationService.confirm({
        key: 'emailValidationConfirm',
        header: 'Email confirmation',
        message: validation.suggestion
          ? `Did you mean <b>${validation.suggestion}</b>?`
          : `Is the email <b>${email}</b> correct?`,
        acceptIcon: 'null',
        accept: () => {
          if (validation.suggestion) {
            this.form.patchValue({ email: validation.suggestion });
          }
          this.register();
        },
        rejectIcon: 'null',
        reject: (type: ConfirmEventType) => {
          if (type === ConfirmEventType.REJECT && validation.suggestion) {
            this.register();
            return;
          }
          this.submitting = false;
        }
      });
    });
  }

  private register(): void {
    const profile: RegistrationProfile = {
      firstName: this.form.value.firstName,
      lastName: this.form.value.lastName,
      email: this.form.value.email,
      password: this.form.value.password,
      referralCode: this._route.snapshot.queryParamMap.get('ref') || undefined
    };

    this._registrationService
      .register(profile)
      .then((result) => {
        if (result.status === 'verify-email') {
          this.done = true;
        } else if (result.status === 'complete') {
          if (result.tokens) {
            // Establish the session with the freshly minted tokens, then send the
            // new user to the company-details/onboarding step. We navigate
            // explicitly rather than relying on AuthService.login's internal
            // routing (which is tuned for the Okta sign-in callback path).
            this._authService
              .login(result.tokens)
              .then((ok) => {
                this._router.navigate([ok ? '/registration' : '/login'], {
                  queryParams: ok ? undefined : { registered: 1 }
                });
              })
              .catch((e) => {
                // eslint-disable-next-line no-console
                console.error('[signup] post-register login failed:', e);
                this.errors = ['Could not start your session. Please sign in.'];
              });
          } else {
            // No tokens (e.g. email verification still pending) — fall back to login.
            this._router.navigate(['/login'], { queryParams: { registered: 1 } });
          }
        } else {
          this.errors = result.messages;
        }
      })
      .catch(() => {
        this.errors = ['Registration failed. Please try again.'];
      })
      .finally(() => (this.submitting = false));
  }
}
